import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZodError, z } from 'zod';
import { customFetcher } from './customFetcher';
import {
    CommentCreateInput,
    commentCreateInputSchema,
    commentDtoSchema,
    CommentUpdateInput,
    commentUpdateInputSchema,
    DeleteCommentInput,
    deleteCommentInputSchema,
    eventRsvpSchema,
    eventRsvpListResponseSchema,
    IEventComment,
    IEventCommentDto,
    IEventRsvp,
    IReactionDto,
    ReactionCounts,
    ReactionUserNames,
    RsvpApiStatus,
    normalizedReactionSchema,
    reactionDtoSchema,
    RsvpUpsertResponse,
    rsvpUpsertResponseSchema,
    IReaction,
    ReactionKind,
    reactionEmojiSchema,
    ReactionToggleResponse,
    reactionToggleResponseSchema,
    SetRsvpInput,
    setRsvpInputSchema,
    ToggleReactionInput,
    toggleReactionInputSchema,
} from '../Interfaces/IEventInteraction';

const getCurrentUserId = (): number | null => {
    const value = localStorage.getItem('user_id');
    if (!value) {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

export const EVENT_RSVP_QUERY_KEY = (eventId: number) => ['event-rsvps', eventId] as const;
const EVENT_COMMENT_QUERY_KEY = (eventId: number) => ['event-comments', eventId] as const;

type DeleteCommentContext = {
    previousComments?: IEventComment[];
    eventId: number;
};

type ToggleReactionContext = {
    previousComments?: IEventComment[];
    eventId: number;
};

const normalizeComments = (comments: IEventComment[]): IEventComment[] => {
    const byId = new Map<number, IEventComment>();
    const roots: IEventComment[] = [];

    comments.forEach((comment) => {
        byId.set(comment.id, {
            ...comment,
            replies: [],
        });
    });

    byId.forEach((comment) => {
        if (comment.parent_id && byId.has(comment.parent_id)) {
            const parent = byId.get(comment.parent_id);
            if (parent) {
                parent.replies = [...parent.replies, comment];
            }
            return;
        }

        roots.push(comment);
    });

    const sortRecursive = (items: IEventComment[]): IEventComment[] => (
        items
            .map((item) => ({
                ...item,
                replies: sortRecursive(item.replies),
            }))
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    );

    return sortRecursive(roots);
};

const formatZodError = (error: ZodError): string => {
    const issues = error.issues.map((issue) => `${issue.path.join('.') || 'root'}: ${issue.message}`);
    return issues.join(' | ');
};

const toCommentReactions = (input: unknown[]): IReaction[] => {
    const kindToEmoji: Record<string, ReactionKind> = {
        heart: '❤️',
        like: '👍',
        beer: '🍺',
    };

    return input
        .map((item) => reactionDtoSchema.safeParse(item))
        .filter((result): result is { success: true; data: IReactionDto } => result.success)
        .map((result) => {
            const dto = result.data;
            const emoji = dto.emoji ?? (dto.kind ? kindToEmoji[dto.kind] : undefined);
            if (!emoji) {
                return null;
            }
            return normalizedReactionSchema.parse({
                user_id: dto.user_id,
                user_name: dto.user_name ?? '',
                emoji,
            });
        })
        .filter((reaction): reaction is IReaction => Boolean(reaction));
};

const toReactionViewState = (
    reactions: IReaction[],
    currentUserId: number | null,
): { viewerEmoji: ReactionKind | null; counts: ReactionCounts; reactionUserNames: ReactionUserNames } => {
    const counts: ReactionCounts = {
        '❤️': 0,
        '👍': 0,
        '🍺': 0,
    };
    const reactionUserNames: ReactionUserNames = {
        '❤️': [],
        '👍': [],
        '🍺': [],
    };

    reactions.forEach((reaction) => {
        counts[reaction.emoji] += 1;
        if (reaction.user_name) {
            reactionUserNames[reaction.emoji].push(reaction.user_name);
        }
    });

    const viewerEmoji = currentUserId
        ? reactions.find((reaction) => reaction.user_id === currentUserId)?.emoji ?? null
        : null;

    return { viewerEmoji, counts, reactionUserNames };
};

const toEventComment = (comment: IEventCommentDto, currentUserId: number | null): IEventComment => {
    const reactions = toCommentReactions(comment.reactions);
    const { viewerEmoji, counts, reactionUserNames } = toReactionViewState(reactions, currentUserId);

    return {
        id: comment.id,
        calendar_entry_id: comment.calendar_entry_id,
        text: comment.text,
        parent_id: comment.parent_id,
        created_at: comment.created_at,
        updated_at: comment.updated_at ?? null,
        user: comment.user,
        reactions,
        viewerEmoji,
        counts,
        reactionUserNames,
        replies: [],
    };
};

const toCommentTreeInput = (comments: IEventCommentDto[], currentUserId: number | null): IEventComment[] => (
    comments.map((comment) => toEventComment(comment, currentUserId))
);

const toUiRsvpStatus = (status: RsvpApiStatus): IEventRsvp['status'] => {
    if (status === 'GOING') {
        return 'yes';
    }
    if (status === 'NOT_GOING') {
        return 'no';
    }
    return 'maybe';
};

const toApiRsvpStatus = (status: SetRsvpInput['status']): RsvpApiStatus | null => {
    if (status === 'yes') {
        return 'GOING';
    }
    if (status === 'no') {
        return 'NOT_GOING';
    }
    if (status === 'maybe') {
        return 'MAYBE';
    }
    return null;
};

const removeCommentFromTree = (comments: IEventComment[], commentId: number): IEventComment[] => (
    comments
        .filter((comment) => comment.id !== commentId)
        .map((comment) => ({
            ...comment,
            replies: removeCommentFromTree(comment.replies, commentId),
        }))
);

const patchCommentTree = (
    comments: IEventComment[],
    commentId: number,
    patcher: (comment: IEventComment) => IEventComment,
): IEventComment[] => comments.map((comment) => {
    if (comment.id === commentId) {
        return patcher(comment);
    }

    return {
        ...comment,
        replies: patchCommentTree(comment.replies, commentId, patcher),
    };
});

const applyOptimisticReactionToggle = (comment: IEventComment, emoji: ReactionKind): IEventComment => {
    const previousViewerEmoji = comment.viewerEmoji;
    const nextViewerEmoji = previousViewerEmoji === emoji ? null : emoji;
    const nextCounts: ReactionCounts = { ...comment.counts };

    if (previousViewerEmoji) {
        nextCounts[previousViewerEmoji] = Math.max(0, nextCounts[previousViewerEmoji] - 1);
    }
    if (nextViewerEmoji) {
        nextCounts[nextViewerEmoji] += 1;
    }

    return {
        ...comment,
        viewerEmoji: nextViewerEmoji,
        counts: nextCounts,
    };
};

export const useEventRsvps = (eventId: number) => {
    const { data = [], isLoading, isError, refetch } = useQuery<IEventRsvp[]>({
        queryKey: EVENT_RSVP_QUERY_KEY(eventId),
        queryFn: async () => {
            const fetchedData = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${eventId}/rsvps/`,
            });

            const parsedData = eventRsvpListResponseSchema.parse(fetchedData);
            return parsedData.rsvps.map((rsvp) => eventRsvpSchema.parse({
                event_id: parsedData.eventId,
                user: {
                    id: rsvp.user.id,
                    first_name: rsvp.user.first_name ?? '',
                    last_name: rsvp.user.last_name ?? '',
                    username: rsvp.user.email ?? '',
                    profile_picture: null,
                },
                status: toUiRsvpStatus(rsvp.status),
                updated_at: rsvp.updatedAt,
                guest_count: rsvp.guest_count,
                participants: rsvp.participants,
            }));
        },
        refetchOnWindowFocus: false,
        enabled: eventId > 0,
        initialData: [],
    });

    return {
        rsvps: data,
        isLoadingEventRsvps: isLoading,
        isErrorEventRsvps: isError,
        refetchEventRsvps: refetch,
    };
};

export const useSetEventRsvp = () => {
    const queryClient = useQueryClient();

    return useMutation<RsvpUpsertResponse, Error, SetRsvpInput, { previousRsvps?: IEventRsvp[] }>({
        mutationFn: async (input) => {
            const parsedInput = setRsvpInputSchema.parse(input);

            const body: Record<string, unknown> = {
                status: toApiRsvpStatus(parsedInput.status),
            };
            if (parsedInput.participants !== undefined) {
                body.participants = parsedInput.participants;
            }

            const response = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${parsedInput.eventId}/rsvp/`,
                method: 'PUT',
                body,
            });

            return rsvpUpsertResponseSchema.parse(response);
        },
        onMutate: async (input) => {
            const parsedInput = setRsvpInputSchema.parse(input);
            const userId = getCurrentUserId();
            const key = EVENT_RSVP_QUERY_KEY(parsedInput.eventId);

            await queryClient.cancelQueries({ queryKey: key });

            const previousRsvps = queryClient.getQueryData<IEventRsvp[]>(key);

            if (previousRsvps && userId) {
                const hasExisting = previousRsvps.some((rsvp) => rsvp.user.id === userId);
                let nextRsvps: IEventRsvp[] = previousRsvps;
                if (hasExisting) {
                    if (parsedInput.status === null) {
                        nextRsvps = previousRsvps.filter((rsvp) => rsvp.user.id !== userId);
                    } else {
                        const statusValue = parsedInput.status;
                        nextRsvps = previousRsvps.map((rsvp) => (
                            rsvp.user.id === userId
                                ? { ...rsvp, status: statusValue }
                                : rsvp
                        ));
                    }
                }

                queryClient.setQueryData<IEventRsvp[]>(key, nextRsvps);
            }

            return { previousRsvps };
        },
        onError: (_error, input, context) => {
            const parsedInput = setRsvpInputSchema.parse(input);
            if (context?.previousRsvps) {
                queryClient.setQueryData(EVENT_RSVP_QUERY_KEY(parsedInput.eventId), context.previousRsvps);
            }
        },
        onSettled: (_result, _error, input) => {
            const parsedInput = setRsvpInputSchema.parse(input);
            void queryClient.invalidateQueries({ queryKey: EVENT_RSVP_QUERY_KEY(parsedInput.eventId) });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
};

export const useEventComments = (eventId: number) => {
    const { data = [], isLoading, isError, error, refetch } = useQuery<IEventComment[], Error>({
        queryKey: EVENT_COMMENT_QUERY_KEY(eventId),
        queryFn: async () => {
            const currentUserId = getCurrentUserId();
            const fetchedData = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${eventId}/comments/`,
            });

            try {
                const parsedData = z.array(commentDtoSchema).parse(fetchedData);
                return normalizeComments(toCommentTreeInput(parsedData, currentUserId));
            } catch (parseError) {
                if (parseError instanceof ZodError) {
                    throw new Error(`Kommentar-DTO ungültig: ${formatZodError(parseError)}`);
                }
                throw parseError;
            }
        },
        refetchOnWindowFocus: false,
        enabled: eventId > 0,
        initialData: [],
    });

    return {
        comments: data,
        isLoadingEventComments: isLoading,
        isErrorEventComments: isError,
        errorEventComments: error,
        refetchEventComments: refetch,
    };
};

export const useCreateComment = () => {
    const queryClient = useQueryClient();

    return useMutation<IEventCommentDto, Error, CommentCreateInput>({
        mutationFn: async (input) => {
            const parsedInput = commentCreateInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${parsedInput.event_id}/comments/`,
                method: 'POST',
                body: {
                    text: parsedInput.text,
                    parent_id: parsedInput.parent_id ?? null,
                },
            });

            try {
                return commentDtoSchema.parse(response);
            } catch (parseError) {
                if (parseError instanceof ZodError) {
                    throw new Error(`Kommentar-DTO ungültig: ${formatZodError(parseError)}`);
                }
                throw parseError;
            }
        },
        onSuccess: (createdComment, variables) => {
            const currentUserId = getCurrentUserId();
            queryClient.setQueryData<IEventComment[]>(
                EVENT_COMMENT_QUERY_KEY(variables.event_id),
                (previousComments = []) => normalizeComments([
                    ...previousComments,
                    toEventComment(createdComment, currentUserId),
                ]),
            );
            void queryClient.invalidateQueries({ queryKey: EVENT_COMMENT_QUERY_KEY(variables.event_id) });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
};

export const useUpdateComment = () => {
    const queryClient = useQueryClient();

    return useMutation<IEventCommentDto, Error, CommentUpdateInput & { eventId: number }>({
        mutationFn: async (input) => {
            const parsedInput = commentUpdateInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/comments/${parsedInput.comment_id}`,
                method: 'PUT',
                body: {
                    text: parsedInput.text,
                },
            });

            try {
                return commentDtoSchema.parse(response);
            } catch (parseError) {
                if (parseError instanceof ZodError) {
                    throw new Error(`Kommentar-DTO ungültig: ${formatZodError(parseError)}`);
                }
                throw parseError;
            }
        },
        onSuccess: (_updatedComment, variables) => {
            void queryClient.invalidateQueries({ queryKey: EVENT_COMMENT_QUERY_KEY(variables.eventId) });
        },
    });
};

export const useDeleteComment = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, DeleteCommentInput, DeleteCommentContext>({
        mutationFn: async (input) => {
            const parsedInput = deleteCommentInputSchema.parse(input);
            await customFetcher<void>({
                endpoint: `/comments/${parsedInput.comment_id}`,
                method: 'DELETE',
            });
        },
        onMutate: async (input) => {
            const parsedInput = deleteCommentInputSchema.parse(input);
            const key = EVENT_COMMENT_QUERY_KEY(parsedInput.event_id);
            await queryClient.cancelQueries({ queryKey: key });

            const previousComments = queryClient.getQueryData<IEventComment[]>(key);
            if (previousComments) {
                queryClient.setQueryData<IEventComment[]>(key, removeCommentFromTree(previousComments, parsedInput.comment_id));
            }

            return { previousComments, eventId: parsedInput.event_id };
        },
        onError: (_error, _input, context) => {
            if (context?.previousComments) {
                queryClient.setQueryData(EVENT_COMMENT_QUERY_KEY(context.eventId), context.previousComments);
            }
        },
        onSettled: (_result, _error, variables) => {
            void queryClient.invalidateQueries({ queryKey: EVENT_COMMENT_QUERY_KEY(variables.event_id) });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
};

export const useToggleReaction = () => {
    const queryClient = useQueryClient();

    return useMutation<ReactionToggleResponse, Error, ToggleReactionInput, ToggleReactionContext>({
        mutationFn: async (input) => {
            const parsedInput = toggleReactionInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/comments/${parsedInput.comment_id}/reactions`,
                method: 'POST',
                body: {
                    emoji: parsedInput.emoji,
                },
            });
            return reactionToggleResponseSchema.parse(response);
        },
        onMutate: async (input) => {
            const parsedInput = toggleReactionInputSchema.parse(input);
            const key = EVENT_COMMENT_QUERY_KEY(parsedInput.event_id);

            await queryClient.cancelQueries({ queryKey: key });
            const previousComments = queryClient.getQueryData<IEventComment[]>(key);

            if (previousComments) {
                queryClient.setQueryData<IEventComment[]>(
                    key,
                    patchCommentTree(previousComments, parsedInput.comment_id, (comment) =>
                        applyOptimisticReactionToggle(comment, parsedInput.emoji),
                    ),
                );
            }

            return { previousComments, eventId: parsedInput.event_id };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousComments) {
                queryClient.setQueryData(EVENT_COMMENT_QUERY_KEY(context.eventId), context.previousComments);
            }
        },
        onSuccess: (result, variables) => {
            const key = EVENT_COMMENT_QUERY_KEY(variables.event_id);
            const currentUserId = getCurrentUserId();
            const parsedReactions = toCommentReactions(result.reactions);
            const reactionViewState = toReactionViewState(parsedReactions, currentUserId);
            queryClient.setQueryData<IEventComment[]>(
                key,
                (previousComments = []) => patchCommentTree(previousComments, result.comment_id, (comment) => ({
                    ...comment,
                    reactions: parsedReactions,
                    viewerEmoji: result.viewer_emoji,
                    counts: result.counts,
                    reactionUserNames: reactionViewState.reactionUserNames,
                })),
            );
            void queryClient.invalidateQueries({ queryKey: EVENT_COMMENT_QUERY_KEY(variables.event_id) });
        },
    });
};

export const reactionKindLabel: Record<ReactionKind, string> = {
    '❤️': '❤️',
    '👍': '👍',
    '🍺': '🍺',
};

export const isValidReactionKind = (value: string): value is ReactionKind => (
    reactionEmojiSchema.safeParse(value).success
);
